import { RefObject } from "react";
import { CheckCircle } from "@phosphor-icons/react";

import { PsTextTuner, PsButtonTuner } from "stencilSetup";
import { MfaMethodDto, formatPhoneNumber } from "../utils";
import { defaultTheme } from "theSource/theme";

interface MfaMethodFormProps {
  selectedMethod?: MfaMethodDto;
  isEditing?: boolean;
  handleEdit?: () => void;
  handleSave?: () => void;
  emailInputRef: RefObject<HTMLPsTextTunerElement>;
  smsInputRef: RefObject<HTMLPsTextTunerElement>;
}

export const MfaMethodForm = ({
  selectedMethod,
  isEditing,
  handleEdit,
  handleSave,
  emailInputRef,
  smsInputRef,
}: MfaMethodFormProps) => {
  const methodLookup = selectedMethod?.code;
  const inputId = `mfa-${methodLookup}`;
  const inputName = `mfa-${methodLookup}`;
  const isEmailMethod = methodLookup === 'email';
  const verified = selectedMethod?.preferred || false;
  const selectedMethodData = selectedMethod?.data;
  const selectedMethodPhones = selectedMethodData?.phones || [];
  const selectedMethodEmails = selectedMethodData?.emails || [];

  let label = '';
  switch (methodLookup) {
    case 'email':
      label = 'Email';
      break;
    case 'sms':
      label = 'Phone';
      break;
    case 'yubikey_hard_token':
      label = 'Yubikey';
      break;
    case 'authenticator_app':
      label = 'Authenticator App';
      break;
    default:
      label = 'default';
      break;
  }

  const savedContact =
    selectedMethod?.code === 'email'
      ? selectedMethodEmails?.find((email) => email.preferred)?.email || selectedMethodEmails[0]?.email || ''
      : selectedMethodPhones?.find((phone) => phone.preferred)?.mobile_number || selectedMethodPhones[0]?.mobile_number || '';

  const savedContactString = isEmailMethod ? savedContact : formatPhoneNumber(savedContact);

  return (
    <div className="mt-4">
      {!savedContact || isEditing ? (
        <div className="row align-items-center justify-content-between">
          <div className="col-9">
            <PsTextTuner
              key={`${inputId}-${isEmailMethod}`}
              data-testid={inputId}
              inputId={inputId}
              name={inputName}
              label={label}
              type={isEmailMethod ? 'email' : 'tel'}
              value={savedContact}
              otherData={[
                { attribute: 'autoComplete', value: 'off' },
              ]}
              ref={isEmailMethod ? emailInputRef : smsInputRef}
            />
          </div>
          <div className="col align-self-end text-right">
            <PsButtonTuner
              data-testid="mfa-save-method"
              buttonId="mfa-save-method"
              label="Save"
              level="tertiary"
              onClick={handleSave}
            />
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column">
            <span>
              <div className="d-flex align-items-center">
                {label}
                {verified && (
                  <CheckCircle
                    weight="fill"
                    size={18}
                    color={defaultTheme.palette.success.main}
                    alt=""
                    className="ml-1"
                  />
                )}
              </div>
            </span>
            <span>{savedContactString}</span>
          </div>
          <div className="ml-4 mt-4">
            <PsButtonTuner
              data-testid="mfa-edit-contact"
              buttonId="mfa-edit-contact"
              label="Edit"
              level="tertiary"
              onClick={handleEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
};
